
import React, { useState, useEffect, useMemo } from 'react';
import { LayoutGrid, WalletCards, UsersRound, CircleUser, Plus, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Settings, KeyRound, BellRing } from 'lucide-react';
import { supabase, isSupabaseConfigured } from './lib/supabase';
import { storageService } from './services/storageService';
import { Expense, Category, Group, User as UserType, Tab, Recurrence, ExpenseStatus } from './types';
import { Dashboard } from './components/Dashboard';
import { ExpenseList } from './components/ExpenseList';
import { Groups } from './components/Groups';
import { Profile } from './components/Profile';
import { AddExpenseModal } from './components/AddExpenseModal';
import { Auth } from './components/Auth';
import { Notifications } from './components/Notifications';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [appLoading, setAppLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  
  // Logic for Add/Edit Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editingScope, setEditingScope] = useState<'this' | 'all'>('all');
  
  // Global Date State
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Data State
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [allUsers, setAllUsers] = useState<UserType[]>([]);

  // Apply Dark Mode Class
  useEffect(() => {
    if (currentUser?.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [currentUser?.darkMode]);

  // Auth Listener
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setAppLoading(false);
      return;
    }

    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) setAppLoading(false); 
    }).catch(err => {
      console.error("Auth session check failed:", err);
      setAppLoading(false);
      if (err.message && (err.message.includes('fetch') || err.message.includes('network'))) {
         setInitError('Não foi possível conectar ao servidor. Verifique sua conexão com a internet.');
      } else {
         setInitError('Erro ao inicializar o aplicativo. Tente recarregar.');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) setAppLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch Data when Session exists
  useEffect(() => {
    if (session) {
      fetchData();
    }
  }, [session]);

  const fetchData = async () => {
    try {
      setAppLoading(true);
      const [usr, cats, exps, grps, users] = await Promise.all([
        storageService.getCurrentUser(),
        storageService.getCategories(),
        storageService.getExpenses(),
        storageService.getGroups(),
        storageService.getAllUsers()
      ]);
      
      setCurrentUser(usr);
      setCategories(cats);
      setExpenses(exps);
      setGroups(grps);
      setAllUsers(users);
    } catch (error: any) {
      console.error("Error loading data:", error);
      if (error.message && error.message.includes('fetch')) {
        setInitError("Falha na conexão de dados. O servidor pode estar indisponível.");
      }
    } finally {
      setAppLoading(false);
    }
  };

  const handleSaveExpense = async (expenseData: Partial<Expense>) => {
    try {
      if (editingExpense) {
         // EDIT MODE
         if (editingScope === 'all') {
            await storageService.updateExpense(expenseData);
         } else {
            if (editingExpense.originalExpenseId) {
               const originalDate = new Date(editingExpense.date).toISOString().split('T')[0];
               await storageService.addExclusionDate(editingExpense.originalExpenseId, originalDate);
            } else if (editingExpense.recurrence !== Recurrence.NONE) {
               const originalDate = new Date(editingExpense.date).toISOString().split('T')[0];
               await storageService.addExclusionDate(editingExpense.id, originalDate);
            }

            await storageService.addExpense({
               ...expenseData,
               recurrence: Recurrence.NONE,
               id: undefined
            });
         }
      } else {
         // ADD MODE
         await storageService.addExpense(expenseData);
      }
      
      const freshExpenses = await storageService.getExpenses();
      setExpenses(freshExpenses);
      
      setEditingExpense(null);
      setShowAddModal(false);

    } catch (error) {
      alert('Erro ao salvar despesa. Verifique sua conexão.');
      console.error(error);
    }
  };

  const handleDeleteExpense = async (expense: Expense, scope: 'this' | 'all') => {
      try {
         if (scope === 'all') {
             const idToDelete = expense.originalExpenseId || expense.id;
             await storageService.deleteExpense(idToDelete);
         } else {
             const parentId = expense.originalExpenseId || expense.id;
             const dateToExclude = new Date(expense.date).toISOString().split('T')[0];
             await storageService.addExclusionDate(parentId, dateToExclude);
         }
         
         const freshExpenses = await storageService.getExpenses();
         setExpenses(freshExpenses);
      } catch (error) {
         alert("Erro ao excluir. Verifique sua conexão.");
         console.error(error);
      }
  };

  const handleEditExpenseRequest = (expense: Expense, scope: 'this' | 'all') => {
      setEditingExpense(expense);
      setEditingScope(scope);
      setShowAddModal(true);
  };

  const handleToggleStatus = async (expense: Expense) => {
    try {
      const newStatus = expense.status === ExpenseStatus.PAID 
        ? ExpenseStatus.PENDING 
        : ExpenseStatus.PAID;
      
      setExpenses(prev => prev.map(e => e.id === expense.id ? { ...e, status: newStatus } : e));

      if (expense.isRecurringInstance && expense.originalExpenseId) {
         const dateToExclude = new Date(expense.date).toISOString().split('T')[0];
         await storageService.addExclusionDate(expense.originalExpenseId, dateToExclude);

         await storageService.addExpense({
            description: expense.description,
            amount: expense.amount,
            categoryId: expense.categoryId,
            groupId: expense.groupId,
            date: expense.date,
            status: newStatus,
            recurrence: Recurrence.NONE, 
            paidByUserId: expense.paidByUserId,
            splitEqually: expense.splitEqually,
            type: expense.type,
            id: undefined
         });

         const fresh = await storageService.getExpenses();
         setExpenses(fresh);

      } else {
         await storageService.updateExpenseStatus(expense.id, newStatus);
         const fresh = await storageService.getExpenses();
         setExpenses(fresh);
      }
    } catch (error) {
      console.error("Failed to update status", error);
      fetchData(); 
      alert("Erro ao atualizar status. Verifique sua conexão.");
    }
  };

  const handleToggleFavorite = async (expense: Expense) => {
    if (expense.isRecurringInstance) {
      alert("Não é possível favoritar uma projeção recorrente. Edite-a para salvá-la individualmente.");
      return;
    }

    const newFavoriteStatus = !expense.isFavorite;
    
    // Optimistic Update
    setExpenses(prev => prev.map(e => e.id === expense.id ? { ...e, isFavorite: newFavoriteStatus } : e));

    try {
      await storageService.toggleExpenseFavorite(expense.id, newFavoriteStatus);
    } catch (error) {
      console.error("Failed to toggle favorite", error);
      fetchData(); // Revert on error
    }
  };

  const handleUpdateUser = async (updatedUser: UserType) => {
    try {
      await storageService.saveUser(updatedUser);
      setCurrentUser(updatedUser);
    } catch (error) {
       console.error(error);
       alert("Erro ao salvar perfil.");
    }
  };

  const handleUpdateCategories = async (updatedCategories: Category[]) => {
    setCategories(updatedCategories);
    try {
      await storageService.saveCategories(updatedCategories);
      const refreshed = await storageService.getCategories();
      setCategories(refreshed);
    } catch (error) {
      console.error(error);
      alert("Erro ao salvar categorias.");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await storageService.deleteCategory(categoryId);
      const refreshed = await storageService.getCategories();
      setCategories(refreshed);
    } catch (error) {
      console.error("Erro ao excluir categoria:", error);
      alert("Não foi possível excluir a categoria. Verifique se o banco de dados permite exclusão (ON DELETE SET NULL).");
    }
  };

  const handleGroupChangeWrapper = async (newGroups: Group[]) => {
      try {
        const added = newGroups.find(g => !groups.find(og => og.id === g.id));
        if (added) {
           await storageService.addGroup(added.name, added.description || '', added.members.map(m => m.id));
        }
        
        const deleted = groups.find(og => !newGroups.find(ng => ng.id === og.id));
        if (deleted) {
           await storageService.deleteGroup(deleted.id);
        }
        
        const updated = newGroups.find(ng => {
           const old = groups.find(og => og.id === ng.id);
           return old && (old.name !== ng.name || old.description !== ng.description || old.members.length !== ng.members.length);
        });
        if (updated) {
           await storageService.updateGroup(updated);
        }
        
        const freshGroups = await storageService.getGroups();
        setGroups(freshGroups);
      } catch (error) {
        console.error(error);
        alert("Erro ao atualizar grupos.");
      }
  };


  const handlePrevMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(selectedDate.getMonth() - 1);
    setSelectedDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(selectedDate.getMonth() + 1);
    setSelectedDate(newDate);
  };

  const isCurrentMonth = () => {
    const now = new Date();
    return selectedDate.getMonth() === now.getMonth() && selectedDate.getFullYear() === now.getFullYear();
  };

  const monthlyExpenses = useMemo(() => {
    const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0, 23, 59, 59);

    const resultExpenses: Expense[] = [];

    expenses.forEach(expense => {
      const expDate = new Date(expense.date);
      const excludeDates = expense.excludeDates || [];
      
      if (expense.recurrence === Recurrence.NONE) {
        if (expDate >= startOfMonth && expDate <= endOfMonth) {
          resultExpenses.push(expense);
        }
        return;
      }

      if (expDate > endOfMonth) return;

      if (expense.recurrence === Recurrence.WEEKLY) {
        const dayOfWeek = expDate.getDay();
        let currentDay = new Date(startOfMonth);
        while (currentDay.getDay() !== dayOfWeek) {
           currentDay.setDate(currentDay.getDate() + 1);
        }
        while (currentDay < expDate) {
            currentDay.setDate(currentDay.getDate() + 7);
        }

        while (currentDay <= endOfMonth) {
           const dateStr = currentDay.toISOString().split('T')[0];
           if (currentDay >= expDate && !excludeDates.includes(dateStr)) {
             resultExpenses.push({
               ...expense,
               id: `${expense.id}-rec-${currentDay.getDate()}`,
               date: currentDay.toISOString(),
               isRecurringInstance: true,
               originalExpenseId: expense.id,
               status: currentDay > new Date() ? 'Pendente' : expense.status as any
             });
           }
           currentDay.setDate(currentDay.getDate() + 7);
        }
      }
      else if (expense.recurrence === Recurrence.MONTHLY) {
        const targetDay = expDate.getDate();
        const daysInCurrentMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
        const actualDay = Math.min(targetDay, daysInCurrentMonth);
        
        const instanceDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), actualDay, 12, 0, 0);
        const dateStr = instanceDate.toISOString().split('T')[0];
        
        const expMonthStr = `${expDate.getFullYear()}-${String(expDate.getMonth()+1).padStart(2, '0')}`;
        const currMonthStr = `${instanceDate.getFullYear()}-${String(instanceDate.getMonth()+1).padStart(2, '0')}`;

        if (currMonthStr >= expMonthStr && !excludeDates.includes(dateStr)) {
          resultExpenses.push({
             ...expense,
             id: `${expense.id}-rec-monthly`,
             date: instanceDate.toISOString(),
             isRecurringInstance: true,
             originalExpenseId: expense.id,
             status: instanceDate > new Date() ? 'Pendente' : expense.status as any
          });
        }
      }
      else if (expense.recurrence === Recurrence.YEARLY) {
         if (expDate.getMonth() === selectedDate.getMonth()) {
            const targetDay = expDate.getDate();
            const instanceDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), targetDay, 12, 0, 0);
            const dateStr = instanceDate.toISOString().split('T')[0];
            
            if (instanceDate.getFullYear() >= expDate.getFullYear() && !excludeDates.includes(dateStr)) {
               resultExpenses.push({
                 ...expense,
                 id: `${expense.id}-rec-yearly`,
                 date: instanceDate.toISOString(),
                 isRecurringInstance: true,
                 originalExpenseId: expense.id,
                 status: instanceDate > new Date() ? 'Pendente' : expense.status as any
               });
            }
         }
      }
    });

    return resultExpenses.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [expenses, selectedDate]);


  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-brand-black flex flex-col items-center justify-center p-8 text-white relative overflow-hidden">
        <div className="relative z-10 max-w-md w-full bg-white/5 border border-white/10 p-8 rounded-[2.5rem] shadow-2xl text-center">
            <div className="w-16 h-16 bg-brand-lime text-brand-dark rounded-2xl mx-auto flex items-center justify-center mb-6 shadow-glow">
              <KeyRound size={32} />
            </div>
            <h2 className="text-2xl font-bold mb-3">Configuração Necessária</h2>
            <p className="text-slate-400 mb-6">Variáveis de ambiente do Supabase não encontradas.</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen bg-brand-black flex flex-col items-center justify-center p-8 text-white text-center">
        <div className="w-20 h-20 bg-red-500/10 rounded-3xl flex items-center justify-center mb-6 text-red-500 animate-pulse">
           <AlertTriangle size={40} />
        </div>
        <h2 className="text-2xl font-bold mb-2">Erro de Conexão</h2>
        <p className="text-slate-400 mb-8 max-w-xs mx-auto leading-relaxed">{initError}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-brand-lime text-brand-dark px-8 py-3 rounded-2xl font-bold hover:opacity-90 transition-opacity"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!session) {
    if (appLoading) return <div className="min-h-screen bg-brand-black flex items-center justify-center"><Loader2 className="text-brand-lime animate-spin" size={40} /></div>;
    return <Auth />;
  }

  if (appLoading || !currentUser) return <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 flex items-center justify-center flex-col gap-4">
    <Loader2 className="text-brand-dark dark:text-brand-lime animate-spin" size={40} />
    <p className="text-slate-400 text-sm font-medium animate-pulse">Carregando seus dados...</p>
  </div>;

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-zinc-950 font-sans text-slate-900 dark:text-slate-100 flex justify-center items-start pt-0 sm:pt-10 transition-colors duration-300">
      <div className="w-full sm:max-w-[400px] bg-white dark:bg-zinc-950 sm:rounded-[40px] h-screen sm:h-[850px] shadow-2xl relative flex flex-col overflow-hidden ring-8 ring-slate-900/5 dark:ring-zinc-800/20">
        
        {/* Month Selector Header */}
        <div className="pt-8 px-6 pb-2 bg-white dark:bg-zinc-950 z-10">
          <div className="flex items-center justify-between bg-slate-50 dark:bg-zinc-900 rounded-2xl p-2">
            <button onClick={handlePrevMonth} className="p-2 text-slate-400 hover:text-brand-dark dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-all shadow-sm">
              <ChevronLeft size={20} />
            </button>
            <div className="flex flex-col items-center">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100 capitalize">
                {selectedDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
              </span>
              {!isCurrentMonth() && (
                <button 
                  onClick={() => setSelectedDate(new Date())}
                  className="text-[10px] font-bold text-brand-lime bg-brand-dark dark:bg-zinc-800 px-2 py-0.5 rounded-md mt-0.5"
                >
                  Voltar para Hoje
                </button>
              )}
            </div>
            <button onClick={handleNextMonth} className="p-2 text-slate-400 hover:text-brand-dark dark:hover:text-white hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-all shadow-sm">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto no-scrollbar bg-white dark:bg-zinc-950">
          <div className="p-6 pb-32">
            {activeTab === 'dashboard' && (
              <div className="animate-in fade-in duration-300">
                 <Dashboard 
                    expenses={monthlyExpenses} 
                    categories={categories} 
                    currentUser={currentUser} 
                    selectedDate={selectedDate}
                    onViewNotifications={() => setActiveTab('notifications')}
                 />
              </div>
            )}
            {activeTab === 'expenses' && (
               <div className="animate-in fade-in duration-300">
                 <ExpenseList 
                    expenses={monthlyExpenses} 
                    categories={categories} 
                    selectedDate={selectedDate}
                    groups={groups}
                    allUsers={allUsers}
                    onToggleStatus={handleToggleStatus}
                    onDeleteExpense={handleDeleteExpense}
                    onEditExpense={handleEditExpenseRequest}
                    onToggleFavorite={handleToggleFavorite}
                 />
               </div>
            )}
            {activeTab === 'groups' && (
               <div className="animate-in fade-in duration-300">
                 <Groups 
                    groups={groups} 
                    currentUser={currentUser}
                    allUsers={allUsers}
                    expenses={expenses}
                    monthlyExpenses={monthlyExpenses}
                    selectedDate={selectedDate}
                    onUpdateGroups={handleGroupChangeWrapper}
                    categories={categories}
                    onToggleStatus={handleToggleStatus}
                 />
               </div>
            )}
            {activeTab === 'notifications' && (
               <div className="animate-in fade-in duration-300">
                 <Notifications 
                    expenses={monthlyExpenses}
                    categories={categories}
                    groups={groups}
                    currentUser={currentUser}
                    allUsers={allUsers}
                    selectedDate={selectedDate}
                 />
               </div>
            )}
            {activeTab === 'profile' && (
               <div className="animate-in fade-in duration-300">
                 <Profile 
                    currentUser={currentUser} 
                    categories={categories} 
                    monthlyExpenses={monthlyExpenses}
                    onUpdateUser={handleUpdateUser}
                    onUpdateCategories={handleUpdateCategories}
                    onDeleteCategory={handleDeleteCategory}
                    selectedDate={selectedDate}
                 />
               </div>
            )}
          </div>
        </main>

        {/* Custom Modern Bottom Navigation with Liquid Curve */}
        <div className="absolute bottom-6 left-4 right-4 z-20">
          <div className="relative bg-brand-black dark:bg-black rounded-[2rem] px-1 h-[4.5rem] flex items-center justify-between shadow-2xl shadow-brand-lime/5">
            
            {/* The Hump (Curve) Implementation */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-6 w-20 h-20 bg-brand-black dark:bg-black rounded-full pointer-events-none"></div>
            
            <div className="absolute left-[calc(50%-2.5rem-1.25rem)] -top-0 w-5 h-5 rounded-tr-[20px] shadow-[4px_-4px_0_0_#09090b] dark:shadow-[4px_-4px_0_0_#000000] pointer-events-none"></div>
            <div className="absolute left-[calc(50%+2.5rem)] -top-0 w-5 h-5 rounded-tl-[20px] shadow-[-4px_-4px_0_0_#09090b] dark:shadow-[-4px_-4px_0_0_#000000] pointer-events-none"></div>

            {/* Icons Left */}
            <div className="flex items-center gap-2 pl-4">
              <button 
                onClick={() => setActiveTab('dashboard')}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 ${activeTab === 'dashboard' ? 'text-brand-lime' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                <LayoutGrid size={24} strokeWidth={activeTab === 'dashboard' ? 2.5 : 2} />
              </button>
              <button 
                onClick={() => setActiveTab('expenses')}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 ${activeTab === 'expenses' ? 'text-brand-lime' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                <WalletCards size={24} strokeWidth={activeTab === 'expenses' ? 2.5 : 2} />
              </button>
            </div>

            {/* Spacer for Center Button */}
            <div className="w-16"></div>

            {/* Center Floating Button */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
               <button 
                 onClick={() => {
                    setEditingExpense(null);
                    setShowAddModal(true);
                 }}
                 className="w-14 h-14 bg-brand-lime text-brand-black rounded-full shadow-glow flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
               >
                 <Plus size={28} strokeWidth={2.5} />
               </button>
            </div>

            {/* Icons Right */}
            <div className="flex items-center gap-2 pr-4">
               <button 
                onClick={() => setActiveTab('groups')}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 ${activeTab === 'groups' ? 'text-brand-lime' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                <UsersRound size={24} strokeWidth={activeTab === 'groups' ? 2.5 : 2} />
              </button>
              <button 
                onClick={() => setActiveTab('profile')}
                className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 ${activeTab === 'profile' ? 'text-brand-lime' : 'text-zinc-500 hover:text-zinc-300'}`}
              >
                <CircleUser size={24} strokeWidth={activeTab === 'profile' ? 2.5 : 2} />
              </button>
            </div>

          </div>
        </div>

        {/* Modal */}
        {showAddModal && (
          <AddExpenseModal 
            categories={categories} 
            groups={groups} 
            currentUser={currentUser.id}
            onClose={() => {
               setShowAddModal(false);
               setEditingExpense(null);
            }} 
            onSave={handleSaveExpense}
            expenseToEdit={editingExpense}
          />
        )}
      </div>
    </div>
  );
};

export default App;
